import axios from 'axios';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  midtrans_environment: string;
  midtrans_server_key: string;
  midtrans_client_key: string;
  email_from: string;
  email_reply_to: string;
  whatsapp_enabled: boolean;
  max_orders_per_month: number;
  max_admin_users: number;
  primary_color: string;
  logo_url: string;
  domain_custom: string | null;
  created_at: string;
  updated_at: string;
  totalOrders?: number;
  paidOrders?: number;
  pendingOrders?: number;
  failedOrders?: number;
  totalRevenue?: number;
}

export interface DashboardStats {
  totalOrders: number;
  paidOrders: number;
  pendingOrders: number;
  failedOrders: number;
  totalRevenue: number;
  conversionRate: number;
}

export interface TenantAlert {
  id: number;
  tenantSlug: string;
  eventType: string;
  severity: string;
  title: string;
  message: string;
  payload?: Record<string, unknown> | null;
  status: string;
  createdAt: string;
  sentAt?: string | null;
  acknowledgedAt?: string | null;
  resolvedAt?: string | null;
}

export interface Order {
  id: string;
  order_id: string;
  tenant_id: string;
  customer_name: string;
  email: string;
  phone: string;
  amount: number;
  payment_method: string;
  payment_status: string;
  created_at: string;
  paid_at: string | null;
}

// Tenant API
export const tenantApi = {
  getAll: async (): Promise<{ tenants: Tenant[] }> => {
    const response = await api.get('/tenants');
    return response.data;
  },

  getById: async (id: string): Promise<{ tenant: Tenant }> => {
    const response = await api.get(`/tenants/${id}`);
    return response.data;
  },

  create: async (data: Partial<Tenant>): Promise<{ tenant: Tenant }> => {
    const response = await api.post('/tenants', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Tenant>): Promise<{ tenant: Tenant }> => {
    const response = await api.put(`/tenants/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/tenants/${id}`);
  },

  getStats: async (tenantId: string): Promise<{ stats: DashboardStats }> => {
    const response = await api.get(`/admin/stats?tenantId=${tenantId}`);
    return response.data;
  },

  getAlerts: async (tenantId: string, limit = 10): Promise<{ alerts: TenantAlert[] }> => {
    const response = await api.get(`/admin/alerts?tenantId=${tenantId}&limit=${limit}`);
    return response.data;
  },

  acknowledgeAlert: async (alertId: number): Promise<{ alert: TenantAlert }> => {
    const response = await api.patch(`/admin/alerts/${alertId}/acknowledge`);
    return response.data;
  },

  resolveAlert: async (alertId: number): Promise<{ alert: TenantAlert }> => {
    const response = await api.patch(`/admin/alerts/${alertId}/resolve`);
    return response.data;
  },

  reportWebhookFailure: async (data: {
    tenantId: string;
    orderId?: string;
    providerEndpoint: string;
    errorDetail: string;
  }): Promise<{ success: boolean }> => {
    const response = await api.post('/admin/alerts/report-webhook-failure', data);
    return response.data;
  },
};

// Order API
export const orderApi = {
  getAll: async (tenantId?: string, params?: any): Promise<{ orders: Order[] }> => {
    const response = await api.get('/orders', {
      params: { ...params, tenantId }
    });
    return response.data;
  },

  getById: async (id: string): Promise<{ order: Order }> => {
    const response = await api.get(`/orders/${id}`);
    return response.data;
  },

  updateStatus: async (id: string, status: string): Promise<{ order: Order }> => {
    const response = await api.patch(`/orders/${id}/status`, { status });
    return response.data;
  },
};

// Auth API
export const authApi = {
  login: async (email: string, password: string): Promise<{ token: string }> => {
    const response = await api.post('/admin/login', { email, password });
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/admin/logout');
  },
};

export default api;
